import React, { useEffect } from 'react'
import { initializeNostoSessionParams, nostoSessionParams } from '@stores/nostoSessionStore'

const NostoSessionInit = () => {
    useEffect(() => {
        // Helper function to check if a cookie exists
        const checkCookie = (cookieName) => {
            const cookies = document.cookie.split('; ')
            console.log('🔍 All cookies:', cookies.map(c => c.split('=')[0]).join(', '))
            return cookies.some(cookie => cookie.startsWith(`${cookieName}=`))
        }

        // Wait for Nosto's internal cookie to be set
        const waitForNostoCookie = () => {
            return new Promise((resolve) => {
                const cookieName = '2c.cId'
                let attempts = 0
                const maxAttempts = 50 // 5 seconds total (50 * 100ms)

                console.log('🔍 NostoSessionInit - Waiting for Nosto cookie:', cookieName)

                const checkInterval = setInterval(() => {
                    attempts++

                    if (checkCookie(cookieName)) {
                        clearInterval(checkInterval)
                        console.log('✅ NostoSessionInit - Nosto cookie found after', attempts * 100, 'ms')
                        resolve(true)
                    } else if (attempts >= maxAttempts) {
                        clearInterval(checkInterval)
                        console.warn('⚠️ NostoSessionInit - Timeout waiting for Nosto cookie, proceeding anyway')
                        console.log('🔍 Current domain:', window.location.hostname)
                        console.log('🔍 Is nostojs available?', typeof window.nostojs !== 'undefined')
                        resolve(false)
                    }
                }, 100)
            })
        }

        // Wait for Nosto SDK to be fully loaded
        const waitForNostoSDK = () => {
            return new Promise((resolve) => {
                // Check if SDK is already loaded
                if (window.nostoSDKReady) {
                    console.log('✅ Nosto SDK already ready')
                    resolve()
                    return
                }

                console.log('⏳ Waiting for Nosto SDK to load...')

                // Listen for SDK ready event
                const handleSDKReady = () => {
                    console.log('✅ Nosto SDK ready event received')
                    window.removeEventListener('nostoSDKReady', handleSDKReady)
                    resolve()
                }

                window.addEventListener('nostoSDKReady', handleSDKReady)

                // Fallback timeout
                setTimeout(() => {
                    window.removeEventListener('nostoSDKReady', handleSDKReady)
                    console.warn('⚠️ Timeout waiting for Nosto SDK, proceeding anyway')
                    resolve()
                }, 10000) // 10 second timeout
            })
        }

        const initSession = async () => {
            try {
                // Wait for Nosto SDK script to fully load
                await waitForNostoSDK()

                // Try to manually initialize Nosto session to trigger cookie creation
                if (window.nostojs) {
                    console.log('🔧 Attempting to manually trigger Nosto session initialization...')
                    window.nostojs((api) => {
                        try {
                            // This should trigger Nosto to create the customer ID cookie
                            api.defaultSession()
                            console.log('✅ Nosto session API called')
                        } catch (err) {
                            console.error('❌ Error calling Nosto session API:', err)
                        }
                    })

                    // Wait a bit for the session to initialize
                    await new Promise(resolve => setTimeout(resolve, 1000))
                }

                // Then wait for Nosto's internal cookie to be set
                const cookieFound = await waitForNostoCookie()

                if (!cookieFound) {
                    // Check if GPC is enabled
                    const gpcEnabled = navigator.globalPrivacyControl === true
                    if (gpcEnabled) {
                        console.log('ℹ️ NostoSessionInit - Cookie blocked by Global Privacy Control (GPC) - this is expected')
                    } else {
                        console.warn('⚠️ NostoSessionInit - Cookie not found, but proceeding with initialization')
                    }
                }

                // Initialize session params
                await initializeNostoSessionParams()

                // Save to cookie for server-side access
                const params = nostoSessionParams.get()
                if (params) {
                    const cookieValue = encodeURIComponent(JSON.stringify(params))
                    document.cookie = `nosto_session_params=${cookieValue}; path=/; max-age=3600; SameSite=Lax`
                    console.log('✅ NostoSessionInit - Session initialized with params:', params)
                } else {
                    console.log('ℹ️ NostoSessionInit - Session initialized (no params)')
                }
            } catch (error) {
                console.error('❌ Error initializing Nosto session:', error)
            }
        }

        initSession()
    }, [])

    return null // This component doesn't render anything
}

export default NostoSessionInit
